import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Chess } from 'chess.js';
import { api } from '@/lib/api';
import ChessBoardView from '@/components/chess/ChessBoardView';
import StockfishPanel from '@/components/chess/StockfishPanel';
import CoachPanel from '@/components/chess/CoachPanel';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Play, Rewind } from 'lucide-react';

export default function GameViewer() {
  const { id } = useParams();
  const [game, setGame] = useState(null);
  const [moves, setMoves] = useState([]);
  const [positions, setPositions] = useState([]); // precomputed FEN sequence
  const [ply, setPly] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.get(`/games/${id}`)
      .then(r => {
        if (cancelled) return;
        setGame(r.data);
        // Parse PGN once; build authoritative FEN sequence step-by-step from initial position.
        // Each ply's FEN becomes the guaranteed starting position of the next move.
        const c = new Chess();
        try { c.loadPgn(r.data.pgn); } catch {}
        const hist = c.history({ verbose: true });
        const c2 = new Chess();
        const fens = [c2.fen()];
        for (const m of hist) {
          try { c2.move(m.san); fens.push(c2.fen()); }
          catch { break; } // stop precomputing at first illegal token, keep prior positions
        }
        if (cancelled) return;
        setMoves(hist.slice(0, fens.length - 1));
        setPositions(fens);
        setPly(fens.length - 1);
      })
      .catch(() => { /* silent: componente ya se desmontó o error de red */ });
    return () => { cancelled = true; };
  }, [id]);

  const positionFen = useMemo(() => {
    if (!positions.length) return new Chess().fen();
    const idx = Math.min(Math.max(0, ply), positions.length - 1);
    return positions[idx];
  }, [positions, ply]);

  if (!game) return <div className="p-8">Cargando…</div>;

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-4">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Partida · {game.event}</div>
        <h1 className="font-display text-3xl font-extrabold" data-testid="game-title">
          {game.white_player} <span className="text-slate-400">vs</span> {game.black_player}
        </h1>
        <div className="text-sm text-slate-500 mt-1 font-mono">{game.result} · {game.date} · ECO {game.eco || '—'} · {game.opening || ''}</div>
      </div>
      <div className="grid lg:grid-cols-5 gap-8">
        <div className="lg:col-span-3">
          <ChessBoardView fen={positionFen} boardWidth={560} arePiecesDraggable={false}/>
          <div className="mt-4 flex items-center gap-2">
            <Button onClick={()=>setPly(0)} variant="outline" size="icon" className="rounded-full"><Rewind className="w-4 h-4"/></Button>
            <Button data-testid="prev-move" onClick={()=>setPly(p => Math.max(0, p-1))} variant="outline" size="icon" className="rounded-full"><ChevronLeft className="w-4 h-4"/></Button>
            <Button data-testid="next-move" onClick={()=>setPly(p => Math.min(moves.length, p+1))} variant="outline" size="icon" className="rounded-full"><ChevronRight className="w-4 h-4"/></Button>
            <Button onClick={()=>setPly(moves.length)} variant="outline" size="icon" className="rounded-full"><Play className="w-4 h-4"/></Button>
            <div className="ml-3 text-sm font-mono text-slate-600">Jugada {ply}/{moves.length}</div>
          </div>
        </div>
        <div className="lg:col-span-2 space-y-4">
          <StockfishPanel fen={positionFen} depth={15}/>
          <CoachPanel fen={positionFen} last_move={moves[ply-1]?.san} pgn={game?.pgn}/>
          <div className="rounded-2xl bg-white border border-slate-200 p-4">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Movimientos</div>
            <div className="font-mono text-sm text-slate-700 max-h-60 overflow-y-auto leading-relaxed">
              {moves.map((m,i)=>(
                <span key={i} onClick={()=>setPly(i+1)}
                  className={`cursor-pointer px-1 rounded ${i<ply ? 'bg-slate-100' : ''} ${i+1===ply ? 'bg-amber-200' : ''}`}>
                  {i%2===0 ? `${Math.floor(i/2)+1}.` : ''}{m.san}{' '}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
